import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Close from '../../assets/svg/CloseButton.svg';
import { useState, useEffect, useMemo, useRef } from 'react';
import Loader from '../Live/Loader';
import axios from 'axios';
import { API } from '../../shared/api/Api';
import { showToast } from '../../utils/utils';
import { useSelector } from 'react-redux';
import { selectUserInformations } from '../../features/authSlice';
import './AddPermissionToRole.css'
import { motion, AnimatePresence, LayoutGroup } from "framer-motion";


// Animation variants
const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 0.3, ease: "easeInOut" }
    }
};

const moduleVariants = {
    hidden: { opacity: 0 },
    visible: {
        opacity: 1,
        transition: { duration: 0.2 }
    }
};

const permissionItemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
        opacity: 1,
        y: 0,
        transition: { delay: i * 0.05 }
    })
};

const checkboxSpring = {
    type: "spring",
    stiffness: 400,
    damping: 25
};

const AddPermissionToRole = ({ roleDetails, onClose }) => {
    const [allPermissions, setAllPermissions] = useState([]);
    const [selectedPermissions, setSelectedPermissions] = useState([]);
    const [groupedPermissions, setGroupedPermissions] = useState({});
    const [openModules, setOpenModules] = useState(new Set());
    const [searchQuery, setSearchQuery] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [loader, setLoader] = useState(true);
    const user = useSelector(selectUserInformations);
    const selectAllCheckboxRef = useRef(null);

    // Group permissions by module
    useEffect(() => {
        if (allPermissions.length > 0) {
            const groups = allPermissions.reduce((acc, permission) => {
                const [module] = permission.name.split('_');
                acc[module] = acc[module] || [];
                acc[module].push(permission);
                return acc;
            }, {});
            setGroupedPermissions(groups);
            setOpenModules(new Set(Object.keys(groups)));
        }
    }, [allPermissions]);

    // Fetch all permissions
    useEffect(() => {
        const fetchPermissions = async () => {
            try {
                const { data } = await axios.get(API.Roles.app_roles_permissions_all, {
                    headers: { Authorization: `Bearer ${user.token}` }
                });

                if (data.status === 200) {
                    setAllPermissions(data.data);
                    setSelectedPermissions(roleDetails?.permissions?.map(p => p.uuid) || []);
                }
            } catch (error) {
                showToast('Error', 'Failed to fetch permissions', 'error');
            } finally {
                setLoader(false);
            }
        };
        fetchPermissions();
    }, [roleDetails, user.token]);

    // Filtered permissions
    const filteredPermissions = useMemo(() => {
        if (!searchQuery) return groupedPermissions;
        return Object.entries(groupedPermissions).reduce((acc, [module, perms]) => {
            const filtered = perms.filter(p =>
                p.name.toLowerCase().includes(searchQuery.toLowerCase())
            );
            if (filtered.length > 0) acc[module] = filtered;
            return acc;
        }, {});
    }, [groupedPermissions, searchQuery]);

    // Checkbox handlers
    const handlePermissionToggle = (permissionUuid) => {
        setSelectedPermissions(prev =>
            prev.includes(permissionUuid)
                ? prev.filter(uuid => uuid !== permissionUuid)
                : [...prev, permissionUuid]
        );
    };

    const isGroupSelected = (permissions) => {
        return permissions.every(p => selectedPermissions.includes(p.uuid));
    };

    const toggleModule = (module) => {
        setOpenModules(prev => {
            const next = new Set(prev);
            next.has(module) ? next.delete(module) : next.add(module);
            return next;
        });
    };

    const isGroupIndeterminate = (permissions) => {
        const hasSelected = permissions.some(p => selectedPermissions.includes(p.uuid));
        return hasSelected && !isGroupSelected(permissions);
    };

    const toggleGroup = (permissions) => {
        const allInGroup = permissions.map(p => p.uuid);
        if (isGroupSelected(permissions)) {
            setSelectedPermissions(prev => prev.filter(uuid => !allInGroup.includes(uuid)));
        } else {
            setSelectedPermissions(prev => [...new Set([...prev, ...allInGroup])]);
        }
    };

    const toggleSelectAll = () => {
        if (selectedPermissions.length === allPermissions.length) {
            setSelectedPermissions([]);
        } else {
            setSelectedPermissions(allPermissions.map(p => p.uuid));
        }
    };

    // Update select all checkbox state
    useEffect(() => {
        if (selectAllCheckboxRef.current) {
            selectAllCheckboxRef.current.indeterminate =
                selectedPermissions.length > 0 &&
                selectedPermissions.length < allPermissions.length;
        }
    }, [selectedPermissions, allPermissions]);

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const { data } = await axios.post(
                `${API.Roles.app_roles_givePermissionToRole}/${roleDetails.uuid}`,
                { permissions: selectedPermissions },
                { headers: { Authorization: `Bearer ${user.token}` } }
            );
            if (data.status === 200) {
                showToast('Success', 'Permissions updated successfully', 'success');
                onClose();
            }
        } catch (error) {
            showToast('Error', error.response?.data?.message || 'Update failed', 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            className="p-6 space-y-6"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            exit="hidden"
        >
            {/* Header */}
            <div className="flex justify-between items-center pb-4 border-b border-gray-200">
                <div>
                    <h3 className="text-2xl font-bold text-gray-900">
                        Add Permissions to {roleDetails?.name}
                    </h3>
                    <div className="mt-2">
                        <input
                            type="text"
                            value={roleDetails?.uuid || ''}
                            disabled
                            className="px-3 py-2 border rounded-md bg-gray-100 text-gray-600 text-sm w-96"
                        />
                    </div>
                </div>
                <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full">
                    <img className="w-6 h-6" src={Close} alt="Close" />
                </button>
            </div>

            {/* Controls */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div className="w-full sm:w-96">
                    <input
                        type="text"
                        placeholder="Search permissions..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="w-full px-4 py-2 rounded-md border border-gray-300 focus:ring-2 focus:ring-blue-500"
                    />
                </div>
                <div className="flex items-center gap-4">
                    <div className="text-sm text-gray-600">
                        Selected: {selectedPermissions.length}/{allPermissions.length}
                    </div>
                    <label className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            ref={selectAllCheckboxRef}
                            checked={selectedPermissions.length === allPermissions.length}
                            onChange={toggleSelectAll}
                            className="form-checkbox h-5 w-5 text-blue-600 rounded"
                        />
                        <span className="text-sm font-medium text-gray-700">Select All</span>
                    </label>
                </div>
            </div>

            {/* Permissions List */}
            {loader ? (
                <motion.div
                    className="flex justify-center py-8"
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 1 }}
                >
                    <Loader />
                </motion.div>
            ) : (
                <LayoutGroup>
                    <motion.div className="border rounded-lg overflow-hidden">
                        {Object.entries(filteredPermissions).map(([module, permissions]) => (
                            <motion.div
                                key={module}
                                className="border-b last:border-b-0"
                                variants={moduleVariants}
                                initial="hidden"
                                animate="visible"
                                exit="hidden"
                            >
                                <div className="flex items-center bg-gray-50 hover:bg-gray-100">
                                    <label className="flex items-center px-4 py-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isGroupSelected(permissions)}
                                            onChange={() => toggleGroup(permissions)}
                                            className="form-checkbox h-5 w-5 text-blue-600 rounded"
                                            ref={(el) => {
                                                if (el) {
                                                    el.indeterminate = isGroupIndeterminate(permissions);
                                                }
                                            }}
                                        />
                                    </label>
                                    <button
                                        onClick={() => toggleModule(module)}
                                        className="flex-1 flex justify-between items-center px-4 py-3"
                                    >
                                        <div className="flex items-center gap-3">
                                            <span className="text-gray-600">
                                                {openModules.has(module) ? <FiChevronUp /> : <FiChevronDown />}
                                            </span>
                                            <h5 className="font-medium text-gray-700 capitalize">{module}</h5>
                                            <span className="text-sm text-gray-500 bg-white px-2 py-1 rounded">
                                                {permissions.length} permissions
                                            </span>
                                        </div>
                                    </button>
                                </div>

                                <AnimatePresence>
                                    {openModules.has(module) && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{
                                                opacity: 1,
                                                height: "auto",
                                                transition: { duration: 0.3 }
                                            }}
                                            exit={{
                                                opacity: 0,
                                                height: 0,
                                                transition: { duration: 0.2 }
                                            }}
                                            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 p-4 bg-gray-50 overflow-hidden"
                                        >
                                            {permissions.map((permission, index) => (
                                                <motion.label
                                                    key={permission.uuid}
                                                    className="flex items-center p-6 bg-white border rounded-lg hover:border-blue-200 transition-colors cursor-pointer"
                                                    variants={permissionItemVariants}
                                                    custom={index}
                                                    initial="hidden"
                                                    animate="visible"
                                                    exit="hidden"
                                                    whileHover={{ scale: 1.02 }}
                                                    whileTap={{ scale: 0.98 }}
                                                >
                                                    <motion.input
                                                        type="checkbox"
                                                        checked={selectedPermissions.includes(permission.uuid)}
                                                        onChange={() => handlePermissionToggle(permission.uuid)}
                                                        className="form-checkbox h-5 w-5 text-blue-600 rounded"
                                                        transition={checkboxSpring}
                                                        whileTap={{ scale: 0.9 }}
                                                    />
                                                    <PermissionBadge permission={permission} />
                                                </motion.label>
                                            ))}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        ))}

                        {Object.keys(filteredPermissions).length === 0 && (
                            <motion.div
                                className="p-6 text-center text-gray-500"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                            >
                                No permissions found matching your search
                            </motion.div>
                        )}
                    </motion.div>
                </LayoutGroup>
            )}

            {/* Footer */}
            <div className="flex justify-end space-x-4 border-t pt-4">
                <motion.button
                    onClick={onClose}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    Cancel
                </motion.button>
                <motion.button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    whileHover={{ scale: submitting ? 1 : 1.05 }}
                    whileTap={{ scale: submitting ? 1 : 0.95 }}
                >
                    {submitting ? <Loader /> : 'Save Permissions'}
                </motion.button>
            </div>
        </motion.div>
    );
};

const PermissionBadge = ({ permission }) => {
    const [, action] = permission.name.split('_');
    const colorClasses = [
        'bg-red-100 text-red-800 border-red-200',
        'bg-blue-100 text-blue-800 border-blue-200',
        'bg-green-100 text-green-800 border-green-200',
        'bg-yellow-100 text-yellow-800 border-yellow-200',
        'bg-purple-100 text-purple-800 border-purple-200',
    ];

    const hashString = (str) => {
        let hash = 0;
        for (let i = 0; i < str.length; i++) {
            hash = str.charCodeAt(i) + ((hash << 5) - hash);
        }
        return Math.abs(hash) % colorClasses.length;
    };

    const colorClass = colorClasses[hashString(action)] || colorClasses[0];

    return (
        <motion.div
            className="flex items-center gap-2 ml-2"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
        >            <span className={`px-2 py-1 rounded text-xs font-medium border ${colorClass}`}>
                {action}
            </span>
            <span className="text-sm text-gray-600">
                {permission.name.replace(/_/g, ' ')}
            </span>
        </motion.div>
    );
};

export default AddPermissionToRole;